import type { GameContext } from '../core/context';
import { clamp, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import { Layer } from '../physics/types';
import { VEHICLE_SPECS } from '../vehicles/specs';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { CityLayout } from '../world/types';
import type { PedService } from '../peds/types';
import type { PlayerService } from '../player/playerSystem';
import { Pool } from './pool';
import { laneClassWeight, type Connector, type Lane, type RoadNet } from './roadNet';
import type { SignalService } from './signalController';
import type {
  SpawnTrafficOptions,
  TrafficAgentInfo,
  TrafficHold,
  TrafficMode,
  TrafficService,
  TrafficStats,
} from './types';
import type { VehicleGrid } from './vehicleGrid';
import type { ViewTester } from './visibility';
import { CAR_BUMP_MIN_DV } from '../heat/heatModel';

/**
 * Civilian traffic. Cars follow lanes of the RoadNet with pure-pursuit steering and a speed
 * controller that takes the minimum of several "reasons to slow down" (speed limit, corner, stop
 * line, queue ahead, pedestrians, sirens). Output is only `VehicleControls` — physics and the
 * vehicle model do the driving.
 */

// ------------------------------------------------------------------ tunables
export const TRAFFIC_MAX_CARS = 36;
const SPAWN_MIN_R = 52;
const SPAWN_MAX_R = 118;
/** A fresh population (game start, teleport) leaves the player this much room (m). */
const INITIAL_MIN_R = 16;
const DESPAWN_R = 150;
const SPAWN_LANE_GAP = 15;
/** Ambient spawns keep this far (m) from any car travelling the same way, so they don't arrive as a convoy. */
const SPAWN_FOLLOW_GAP = 32;
/** While the focus moves faster than this (m/s) and nobody is wanted, spawns favour the road ahead and hidden cars far behind are recycled. */
const FOCUS_MOVING = 5;
/** Hidden cars this far (m) behind a moving focus are recycled to the road ahead. */
const BEHIND_R = 92;

const NEAR_R = 55;
const MID_R = 100;
const PROBES = 12;
const BRAKE_COMFORT = 5.5;
const BRAKE_FIRM = 8.5;
const SIREN_REACT_R = 42;
/** A siren only makes cars pull over while it moves faster than this (m/s)... */
const SIREN_MIN_SPEED = 3;
/** ...and closes on the car at least this fast (m/s). */
const SIREN_MIN_CLOSING = 1.5;
const MAX_WRECKS = 8;
/** Seconds at the stop line behind something that won't move in the box before taking another exit. */
const BOX_REROUTE_S = 7;
/** How long (s) a junction path stays marked as blocked after a car gave up on it. */
const BOX_BLOCK_TTL = 20;

export class TrafficAgent {
  id = 0;
  vehicle!: Vehicle;
  lane!: Lane;
  next: Lane | null = null;
  connector: Connector | null = null;
  phase: 'lane' | 'junction' = 'lane';
  /** Progress (m) along the lane section or the connector. */
  s = 0;
  connIdx = 0;
  mode: TrafficMode = 'drive';
  hold: TrafficHold = 'none';
  speedFactor = 1;
  shift = 0;
  shiftTarget = 0;
  route: Lane[] | null = null;
  routeIdx = 0;
  stuckTime = 0;
  blockedTime = 0;
  waitTime = 0;
  modeUntil = 0;
  shakenUntil = 0;
  honkReadyAt = 0;
  crimeReadyAt = 0;
  reactReadyAt = 0;
  sirenSeenAt = -99;
  recoverUntil = 0;
  recoverSteer = 1;
  recoveries = 0;
  retire = false;
  tickAcc = 0;
  vF = 0;
  /** Last steering command so recovery can counter-steer. */
  lastSteer = 0;
  bornAt = 0;
  panicUntil = 0;
  jamTime = 0;
  bumps = 0;
  lastBumpAt = -99;
  backoffUntil = 0;
  /** Agent id this car is currently yielding to (diagnostics), 0 = none. */
  yieldTo = 0;
  /** Right-of-way rules are ignored until this sim time (deadlock breaker). */
  forceGoUntil = 0;
  /** Lateral offset (m, + = left of the lane centre) measured at the last tick. */
  lat = 0;
  /** Distance (m) from the bumper to static geometry ahead on the lane, refreshed every few steps. */
  staticGap = Infinity;
  persistent = false;
  /** Temporarily collision-free to resolve a deadlock inside a junction box. */
  ghost = false;
  /** Seconds waiting at the stop line behind a non-traffic obstacle in the box. */
  boxWait = 0;

  reset(): void {
    this.next = null;
    this.connector = null;
    this.phase = 'lane';
    this.s = 0;
    this.connIdx = 0;
    this.mode = 'drive';
    this.hold = 'none';
    this.shift = 0;
    this.shiftTarget = 0;
    this.route = null;
    this.routeIdx = 0;
    this.stuckTime = 0;
    this.blockedTime = 0;
    this.waitTime = 0;
    this.modeUntil = 0;
    this.shakenUntil = 0;
    this.honkReadyAt = 0;
    this.crimeReadyAt = 0;
    this.reactReadyAt = 0;
    this.sirenSeenAt = -99;
    this.recoverUntil = 0;
    this.recoveries = 0;
    this.retire = false;
    this.tickAcc = 0;
    this.vF = 0;
    this.lastSteer = 0;
    this.panicUntil = 0;
    this.jamTime = 0;
    this.bumps = 0;
    this.lastBumpAt = -99;
    this.backoffUntil = 0;
    this.yieldTo = 0;
    this.forceGoUntil = 0;
    this.staticGap = Infinity;
    this.ghost = false;
    this.boxWait = 0;
    this.persistent = false;
  }
}

interface PathSample {
  x: number;
  z: number;
  dx: number;
  dz: number;
}

/** What scanAhead found on the path: the nearest vehicle, pedestrian or static obstacle. */
interface Obstacle {
  gap: number;
  speed: number;
  kind: 'vehicle' | 'ped' | 'static';
  stationary: boolean;
  beyondLine: boolean;
  vehicle: Vehicle | null;
}

export class TrafficSim implements TrafficService {
  readonly roadNet: RoadNet;
  readonly stats: TrafficStats = { trafficMs: 0, spawned: 0, despawned: 0, stuckRecoveries: 0, honks: 0, poolCreated: 0, poolInUse: 0 };
  density = 1;

  private readonly layout: CityLayout;
  private readonly rng: Rng;
  private readonly list: TrafficAgent[] = [];
  private readonly byVehicle = new Map<number, TrafficAgent>();
  private readonly pool = new Pool<TrafficAgent>(() => new TrafficAgent(), (a) => a.reset());
  private nextAgentId = 1;
  private focusOverride: V2 | null = null;
  private readonly spawnLanes: Lane[] = [];
  /** Per-metre spawn weight of each spawn lane (street class x district density). */
  private readonly spawnWeights: number[] = [];
  /** District traffic density of each spawn lane. */
  private readonly spawnDensity: number[] = [];
  /** Spawn lanes with some part inside the spawn ring around `ringAt` (rebuilt when the focus moves). */
  private readonly ring: number[] = [];
  private readonly ringW: number[] = [];
  private ringAt: V2 | null = null;
  private ringInitial = false;
  /** Spawn-target factor for the area around the focus: the yards and terraces get fewer cars (1 = full). */
  private localFactor = 1;
  /** Focus velocity estimated between manage ticks (m/s), for the spawn bias ahead. */
  private readonly focusVel: V2 = { x: 0, z: 0 };
  private lastManageFocus: V2 | null = null;
  private lastManageAt = 0;
  private readonly spawnSpecs: string[] = [];
  private readonly spawnSpecWeights: number[] = [];
  private readonly approachers: TrafficAgent[][] = [];
  private readonly occupants: TrafficAgent[][] = [];
  private touchedNodes: number[] = [];
  private readonly probe: PathSample[] = Array.from({ length: PROBES + 1 }, () => ({ x: 0, z: 0, dx: 0, dz: 1 }));
  private readonly tmp: PathSample = { x: 0, z: 0, dx: 0, dz: 1 };
  private readonly cand: Vehicle[] = [];
  /** scanAhead's grid visitor and its inputs, kept so no closure is built per agent per think. */
  private scanMe: Vehicle | null = null;
  private scanGhost = false;
  private readonly scanVisit = (o: Vehicle): void => {
    if (o === this.scanMe) return;
    if (this.scanGhost && this.byVehicle.has(o.id)) return;
    this.cand.push(o);
  };
  /** scanAhead's pedestrian probe visitor: the probe it is testing and whether anyone was inside its reach. */
  private pedProbe: PathSample = this.tmp;
  private pedReach2 = 0;
  private pedHit = false;
  private readonly pedVisit = (px: number, pz: number): void => {
    if (this.pedHit) return;
    const pr = this.pedProbe;
    if ((px - pr.x) * (px - pr.x) + (pz - pr.z) * (pz - pr.z) < this.pedReach2) this.pedHit = true;
  };
  private readonly scanResult: Obstacle = { gap: 0, speed: 0, kind: 'vehicle', stationary: false, beyondLine: false, vehicle: null };
  /** Junction paths blocked by an abandoned car / wreck, with the sim time the mark expires. */
  private readonly blockedConnectors = new Map<Connector, number>();
  private playerVehicle: Vehicle | null = null;
  private step = 0;
  /** Test hook: called for every spawn with the freshly placed agent. */
  onSpawn: ((a: TrafficAgent) => void) | null = null;

  constructor(
    private readonly ctx: GameContext,
    private readonly vehicles: VehicleService,
    private readonly net: RoadNet,
    private readonly signals: SignalService,
    private readonly grid: VehicleGrid,
    private readonly view: ViewTester,
  ) {
    this.layout = net.layout;
    this.roadNet = net;
    this.rng = ctx.rng.fork('traffic');
    for (let i = 0; i < net.layout.roads.nodes.length; i++) {
      this.approachers.push([]);
      this.occupants.push([]);
    }
    for (const lane of net.lanes) {
      if (lane.alley || lane.length < 16) continue;
      const district = this.layout.districts.find((d) => d.id === lane.districtId);
      const edge = this.layout.roads.edges[lane.edgeId]!;
      this.spawnLanes.push(lane);
      this.spawnWeights.push(laneClassWeight(lane, edge.sidewalk, edge.main) * (0.15 + (district?.trafficDensity ?? 0.5)));
      this.spawnDensity.push(district?.trafficDensity ?? 0.5);
    }
    for (const spec of Object.values(VEHICLE_SPECS)) {
      if (spec.trafficWeight > 0) {
        this.spawnSpecs.push(spec.id);
        this.spawnSpecWeights.push(spec.trafficWeight);
      }
    }
  }

  // ----------------------------------------------------------------- service surface

  /** Live civilian cars (police patrols driven by traffic and wrecks excluded). */
  get active(): number {
    let n = 0;
    for (const a of this.list) if (a.mode !== 'wreck' && !a.vehicle.isPolice) n++;
    return n;
  }

  get target(): number {
    return this.spawnLanes.length === 0 ? 0 : Math.round(TRAFFIC_MAX_CARS * clamp(this.density, 0, 1.5));
  }

  /** Remove every non-persistent civilian car at once (density 0 = empty streets). */
  clearAmbient(): void {
    for (const a of this.list.slice()) if (!a.persistent) this.release(a, true);
  }

  setFocus(pos: V2 | null): void {
    this.focusOverride = pos;
  }

  focus(): V2 {
    if (this.focusOverride) return this.focusOverride;
    const p = this.ctx.services.tryGet('player');
    return p ? p.pos : this.layout.playerSpawn.pos;
  }

  agentOf(vehicle: Vehicle): TrafficAgentInfo | null {
    const a = this.byVehicle.get(vehicle.id);
    return a ? this.info(a) : null;
  }

  agents(): TrafficAgentInfo[] {
    return this.list.map((a) => this.info(a));
  }

  get poolCreated(): number {
    return this.pool.created;
  }
  get poolInUse(): number {
    return this.pool.inUse;
  }

  /** Internal agent list for tests. */
  get rawAgents(): readonly TrafficAgent[] {
    return this.list;
  }

  private info(a: TrafficAgent): TrafficAgentInfo {
    const v = a.vehicle;
    return {
      id: a.id,
      vehicleId: v.id,
      spec: v.spec.id,
      x: +v.pos.x.toFixed(2),
      z: +v.pos.z.toFixed(2),
      speed: +v.speed.toFixed(2),
      laneId: a.lane.id,
      edgeId: a.lane.edgeId,
      mode: a.mode,
      hold: a.hold,
      phase: a.phase,
      stuckFor: +a.stuckTime.toFixed(1),
    };
  }

  clear(): void {
    for (const a of this.list.slice()) this.release(a, true);
  }

  // ----------------------------------------------------------------- spawning

  spawn(opts: SpawnTrafficOptions): Vehicle | null {
    const lane = this.net.laneNear(opts.edgeId, opts.forward, opts.lane ?? 0);
    if (!lane) return null;
    const s = clamp((opts.t ?? 0.5) * lane.length, 0, lane.length);
    const a = this.spawnOn(lane, s, opts.specId ?? this.rng.weighted(this.spawnSpecs, this.spawnSpecWeights), opts.speed);
    if (!a) return null;
    a.persistent = opts.persistent ?? false;
    if (opts.goalNode !== undefined) {
      const route = this.net.findRoute(lane, opts.goalNode);
      if (route) {
        a.route = route;
        a.routeIdx = 0;
        this.chooseNext(a);
      }
    }
    return a.vehicle;
  }

  /** Is the lane slot around (lane, s) free of other traffic and vehicles? */
  slotFree(lane: Lane, s: number, halfLen: number): boolean {
    const px = lane.a.x + lane.dir.x * s;
    const pz = lane.a.z + lane.dir.z * s;
    for (const o of this.list) {
      if (o.lane === lane && o.phase === 'lane' && Math.abs(o.s - s) < SPAWN_LANE_GAP) return false;
      const dx = o.vehicle.pos.x - px;
      const dz = o.vehicle.pos.z - pz;
      if (dx * dx + dz * dz < (halfLen + 4.5) * (halfLen + 4.5)) return false;
    }
    let free = true;
    this.grid.forEachNear(px, pz, halfLen + 6, (v) => {
      if (!free) return;
      const dx = v.pos.x - px;
      const dz = v.pos.z - pz;
      const r = halfLen + v.spec.length / 2 + 1.5;
      if (dx * dx + dz * dz < r * r) free = false;
    });
    return free;
  }

  private spawnOn(lane: Lane, s: number, specId: string, speed?: number): TrafficAgent | null {
    const spec = VEHICLE_SPECS[specId];
    if (!spec) return null;
    if (!this.slotFree(lane, s, spec.length / 2)) return null;
    const px = lane.a.x + lane.dir.x * s;
    const pz = lane.a.z + lane.dir.z * s;
    const heading = Math.atan2(lane.dir.x, lane.dir.z);
    const v = this.vehicles.spawn(specId, { x: px, z: pz }, {
      heading,
      controller: 'ai',
      occupant: { kind: 'ped', pedId: null },
      tags: ['traffic'],
      locked: false,
    });
    const a = this.pool.acquire();
    a.id = this.nextAgentId++;
    a.vehicle = v;
    a.lane = lane;
    a.s = s;
    a.speedFactor = this.rng.range(0.82, 1.06);
    a.bornAt = this.ctx.state.time;
    a.tickAcc = 0;
    v.data.traffic = { agentId: a.id };
    const v0 = speed ?? lane.speedLimit * 0.7 * a.speedFactor;
    v.body.setVelocity({ x: lane.dir.x * v0, z: lane.dir.z * v0 }, 0);
    a.vF = v0;
    this.chooseNext(a);
    this.list.push(a);
    this.byVehicle.set(v.id, a);
    this.stats.spawned++;
    this.onSpawn?.(a);
    return a;
  }

  private setGhost(a: TrafficAgent, on: boolean): void {
    if (a.ghost === on) return;
    a.ghost = on;
    a.vehicle.body.mask = on ? Layer.ALL & ~(Layer.VEHICLE | Layer.NET_SELF) : Layer.ALL;
  }

  private chooseNext(a: TrafficAgent): void {
    let next: Lane | null = null;
    if (a.route && a.routeIdx < a.route.length) {
      const r = a.route[a.routeIdx]!;
      if (r.fromNode === a.lane.toNode) next = r;
      else a.route = null;
    } else if (a.route) {
      a.route = null;
    }
    if (!next) {
      // Civilians keep to the main roads; a patrol on its beat goes anywhere (its routes as before).
      next = this.net.pickSuccessor(a.lane, this.rng, a.vehicle.spec.wheelbase / Math.tan(a.vehicle.spec.steerAngle), !a.vehicle.isPolice);
      // Avoid a junction path somebody already found blocked (abandoned car in the box).
      if (next && this.blockedConnectors.size > 0 && this.isBlocked(this.net.connector(a.lane, next))) {
        const alt = this.net.successors(a.lane).filter((m) => !this.isBlocked(this.net.connector(a.lane, m)));
        if (alt.length > 0) next = alt[this.rng.int(0, alt.length - 1)]!;
      }
    }
    a.next = next;
    a.connector = next ? this.net.connector(a.lane, next) : null;
  }

  private isBlocked(c: Connector): boolean {
    const until = this.blockedConnectors.get(c);
    if (until === undefined) return false;
    if (until > this.ctx.state.time) return true;
    this.blockedConnectors.delete(c);
    return false;
  }

  /**
   * Waiting at the line behind something in the box that will never move (abandoned car, wreck):
   * mark this junction path blocked and take another exit whose path is clear. Returns true if rerouted.
   */
  private rerouteAroundBlock(a: TrafficAgent, halfW: number): boolean {
    const now = this.ctx.state.time;
    if (a.connector) this.blockedConnectors.set(a.connector, now + BOX_BLOCK_TTL);
    const minR = a.vehicle.spec.wheelbase / Math.tan(a.vehicle.spec.steerAngle);
    const opts = this.net.successors(a.lane).slice();
    // Prefer exits the car can actually make in one go, then anything clear.
    opts.sort((m, n) => Number(this.net.connector(a.lane, n).radius * 1.25 >= minR) - Number(this.net.connector(a.lane, m).radius * 1.25 >= minR));
    for (const m of opts) {
      if (m === a.next) continue;
      const c = this.net.connector(a.lane, m);
      if (this.isBlocked(c) || !this.pathClear(a, c, m, halfW)) continue;
      a.next = m;
      a.connector = c;
      a.route = null;
      a.boxWait = 0;
      this.ctx.events.emit('trafficReaction', { vehicleId: a.vehicle.id, kind: 'bypass', pos: { x: a.vehicle.pos.x, z: a.vehicle.pos.z } });
      return true;
    }
    return false;
  }

  /** Is the way from the car's spot through `c` onto the first metres of `next` free of stationary vehicles? */
  private pathClear(a: TrafficAgent, c: Connector, next: Lane, halfW: number): boolean {
    const me = a.vehicle;
    const pts: V2[] = [];
    const lane = a.lane;
    for (let s = Math.max(a.s + me.spec.length / 2, 0); s < lane.length; s += 2) pts.push({ x: lane.a.x + lane.dir.x * s, z: lane.a.z + lane.dir.z * s });
    for (const p of c.pts) pts.push(p);
    for (let s = 2; s <= Math.min(10, next.length); s += 2) pts.push({ x: next.a.x + next.dir.x * s, z: next.a.z + next.dir.z * s });
    let clear = true;
    for (const p of pts) {
      this.grid.forEachNear(p.x, p.z, 6, (o) => {
        if (!clear || o === me || o.speed > 1.5) return;
        const oa = this.byVehicle.get(o.id);
        if (oa && oa.mode !== 'wreck') return; // live traffic moves on eventually
        const sinA = Math.sin(o.body.angle);
        const cosA = Math.cos(o.body.angle);
        const dx = p.x - o.pos.x;
        const dz = p.z - o.pos.z;
        const lx = dx * cosA - dz * sinA;
        const lz = dx * sinA + dz * cosA;
        if (Math.abs(lx) < o.spec.width / 2 + halfW + 0.3 && Math.abs(lz) < o.spec.length / 2 + 0.8) clear = false;
      });
      if (!clear) return false;
    }
    return true;
  }

  private release(a: TrafficAgent, despawnVehicle: boolean): void {
    const i = this.list.indexOf(a);
    if (i >= 0) this.list.splice(i, 1);
    this.byVehicle.delete(a.vehicle.id);
    this.setGhost(a, false);
    if (despawnVehicle) {
      this.vehicles.despawn(a.vehicle);
      this.stats.despawned++;
    } else {
      delete a.vehicle.data.traffic;
      a.vehicle.tags.delete('traffic');
    }
    this.pool.release(a);
  }

  // ----------------------------------------------------------------- hand-over API (police, missions)

  /** Place a car on a specific lane slot (caller decides where; e.g. police off-screen spawns). */
  spawnOnLane(lane: Lane, s: number, specId: string, opts: { persistent?: boolean; speed?: number } = {}): Vehicle | null {
    const a = this.spawnOn(lane, s, specId, opts.speed);
    if (!a) return null;
    a.persistent = opts.persistent ?? false;
    return a.vehicle;
  }

  /** Stop driving this car (another module takes over its controls). The vehicle stays. */
  detach(vehicle: Vehicle): boolean {
    const a = this.byVehicle.get(vehicle.id);
    if (!a) return false;
    this.release(a, false);
    return true;
  }

  /** Start driving an existing car from the nearest lane matching its heading. */
  adopt(vehicle: Vehicle, persistent = true): boolean {
    if (this.byVehicle.has(vehicle.id) || vehicle.destroyed) return this.byVehicle.has(vehicle.id);
    const hit = this.net.nearestLane(vehicle.pos, vehicle.heading) ?? this.net.nearestLane(vehicle.pos);
    if (!hit || Math.abs(hit.lateral) > 12) return false;
    const a = this.pool.acquire();
    a.id = this.nextAgentId++;
    a.vehicle = vehicle;
    a.lane = hit.lane;
    a.s = hit.s;
    a.speedFactor = this.rng.range(0.9, 1.04);
    a.bornAt = this.ctx.state.time;
    a.persistent = persistent;
    vehicle.controller = 'ai';
    if (!vehicle.occupant) vehicle.occupant = { kind: 'ped', pedId: null };
    vehicle.data.traffic = { agentId: a.id };
    vehicle.tags.add('traffic');
    this.chooseNext(a);
    this.list.push(a);
    this.byVehicle.set(vehicle.id, a);
    return true;
  }

  isDriving(vehicle: Vehicle): boolean {
    return this.byVehicle.has(vehicle.id);
  }

  /**
   * Spawn lanes touching the ring around the focus, weighted by their length inside the ring x street
   * class x district density (cached until the focus moves 10 m). Picking from the ring instead of
   * from the whole map (and throwing away whatever falls outside it) keeps refills quick however big
   * the city is, and spreads cars by street length near the player rather than by district-wide odds.
   */
  private ringLanes(initial: boolean): void {
    const f = this.focus();
    if (this.ringAt && this.ringInitial === initial && Math.hypot(f.x - this.ringAt.x, f.z - this.ringAt.z) < 10) return;
    this.ringAt = { x: f.x, z: f.z };
    this.ringInitial = initial;
    this.ring.length = 0;
    this.ringW.length = 0;
    const rMin = initial ? INITIAL_MIN_R : SPAWN_MIN_R;
    const rMax = initial ? SPAWN_MAX_R + 40 : SPAWN_MAX_R;
    let len = 0;
    let dens = 0;
    for (let i = 0; i < this.spawnLanes.length; i++) {
      const lane = this.spawnLanes[i]!;
      // Cheap reject: the whole lane is outside the outer radius.
      const mx = (lane.a.x + lane.b.x) / 2 - f.x;
      const mz = (lane.a.z + lane.b.z) / 2 - f.z;
      if (Math.hypot(mx, mz) > rMax + lane.length / 2) continue;
      // Length of the usable section (6 m off each end) inside the ring, in 4 m steps.
      let inside = 0;
      for (let s = 6; s <= lane.length - 6; s += 4) {
        const d = Math.hypot(lane.a.x + lane.dir.x * s - f.x, lane.a.z + lane.dir.z * s - f.z);
        if (d >= rMin && d <= rMax) inside += 4;
      }
      if (inside <= 0) continue;
      this.ring.push(i);
      this.ringW.push(inside * this.spawnWeights[i]!);
      len += inside;
      dens += inside * this.spawnDensity[i]!;
    }
    // Mean district traffic density along the ring: the Parade (0.8) gets the full count, the
    // terraces and yards (0.4-0.45) about five in six.
    this.localFactor = len > 0 ? clamp(0.65 + (dens / len) * 0.45, 0.8, 1) : 1;
  }

  /** Cars wanted around the focus right now (the global target, thinned on quiet streets). */
  private get localTarget(): number {
    return Math.round(this.target * this.localFactor);
  }

  private trySpawnRandom(initial: boolean): boolean {
    this.ringLanes(initial);
    if (this.ring.length === 0) return false;
    const lane = this.spawnLanes[this.rng.weighted(this.ring, this.ringW)]!;
    const s = this.rng.range(6, Math.max(7, lane.length - 6));
    const x = lane.a.x + lane.dir.x * s;
    const z = lane.a.z + lane.dir.z * s;
    const f = this.focus();
    const d = Math.hypot(x - f.x, z - f.z);
    if (initial) {
      if (d < INITIAL_MIN_R || d > SPAWN_MAX_R + 40) return false;
    } else {
      if (d < SPAWN_MIN_R || d > SPAWN_MAX_R) return false;
      // Driving: most spawns go to the road ahead, where the player is about to look.
      const v = this.focusVel;
      const sp = Math.hypot(v.x, v.z);
      if (this.ctx.state.heat === 0 && sp > FOCUS_MOVING && ((x - f.x) * v.x + (z - f.z) * v.z) / (d * sp) < -0.2 && this.rng.chance(0.75)) return false;
      if (this.view.isVisible(x, z, 5)) return false;
    }
    if (!this.followGapFree(lane, x, z)) return false;
    const specId = this.rng.weighted(this.spawnSpecs, this.spawnSpecWeights);
    return this.spawnOn(lane, s, specId) !== null;
  }

  /** No car travelling the same way within SPAWN_FOLLOW_GAP (on this lane, or the ones before / after it). */
  private followGapFree(lane: Lane, x: number, z: number): boolean {
    const g2 = SPAWN_FOLLOW_GAP * SPAWN_FOLLOW_GAP;
    for (const o of this.list) {
      const dx = o.vehicle.pos.x - x;
      const dz = o.vehicle.pos.z - z;
      if (dx * dx + dz * dz > g2) continue;
      if (o.lane.dir.x * lane.dir.x + o.lane.dir.z * lane.dir.z > 0.7) return false;
    }
    return true;
  }

  // ----------------------------------------------------------------- per-step update

  fixedUpdate(dt: number): void {
    const now = this.ctx.state.time;
    this.step++;
    this.playerVehicle = this.currentPlayerVehicle();

    // Housekeeping: release agents the player took over, or whose vehicle vanished.
    for (let i = this.list.length - 1; i >= 0; i--) {
      const a = this.list[i]!;
      const v = a.vehicle;
      if (!this.vehicles.get(v.id)) {
        this.release(a, false);
      } else if (a.mode !== 'wreck' && (v.controller !== 'ai' || (v.occupant !== null && v.occupant.kind === 'player'))) {
        this.release(a, false);
      }
    }

    this.buildJunctionBuckets();

    const f = this.focus();
    for (let i = 0; i < this.list.length; i++) {
      const a = this.list[i]!;
      a.tickAcc += dt;
      if (a.mode === 'wreck') continue;
      const dx = a.vehicle.pos.x - f.x;
      const dz = a.vehicle.pos.z - f.z;
      const d2 = dx * dx + dz * dz;
      const period = d2 < NEAR_R * NEAR_R ? 1 : d2 < MID_R * MID_R ? 2 : 4;
      if ((this.step + a.id) % period !== 0) continue;
      const adt = a.tickAcc;
      a.tickAcc = 0;
      this.think(a, adt, now);
    }

    if (this.step % 15 === 0) this.manage(now);
  }

  private countAmbient(): number {
    let n = 0;
    for (const a of this.list) if (a.mode !== 'wreck' && !a.persistent && !a.vehicle.isPolice) n++;
    return n;
  }

  private currentPlayerVehicle(): Vehicle | null {
    const p = this.ctx.services.tryGet('player') as PlayerService | undefined;
    if (!p) return null;
    return p.mode === 'driving' ? p.vehicle : null;
  }

  private buildJunctionBuckets(): void {
    for (const n of this.touchedNodes) {
      this.approachers[n]!.length = 0;
      this.occupants[n]!.length = 0;
    }
    this.touchedNodes.length = 0;
    for (const a of this.list) {
      if (a.mode === 'wreck') continue;
      if (a.phase === 'junction') {
        const n = a.lane.toNode;
        if (this.occupants[n]!.length === 0 && this.approachers[n]!.length === 0) this.touchedNodes.push(n);
        this.occupants[n]!.push(a);
      } else if (a.connector && a.lane.length - a.s < 50) {
        const n = a.lane.toNode;
        if (this.occupants[n]!.length === 0 && this.approachers[n]!.length === 0) this.touchedNodes.push(n);
        // A car whose nose is already over the stop line occupies the box even though its centre
        // hasn't crossed yet.
        if (a.lane.length - a.s - a.vehicle.spec.length / 2 < 0.4) this.occupants[n]!.push(a);
        else this.approachers[n]!.push(a);
      }
    }
  }

  // ----------------------------------------------------------------- spawn / despawn management

  private manage(now: number): void {
    const f = this.focus();
    if (this.lastManageFocus && now > this.lastManageAt) {
      const k = 1 / (now - this.lastManageAt);
      const vx = (f.x - this.lastManageFocus.x) * k;
      const vz = (f.z - this.lastManageFocus.z) * k;
      // A teleport is not a velocity.
      if (Math.hypot(vx, vz) < 60) {
        this.focusVel.x += (vx - this.focusVel.x) * 0.5;
        this.focusVel.z += (vz - this.focusVel.z) * 0.5;
      }
    }
    this.lastManageFocus = { x: f.x, z: f.z };
    this.lastManageAt = now;
    const fv = this.focusVel;
    // Not while wanted: a getaway driver would meet a wall of fresh cars on every road ahead.
    const fSpeed = this.ctx.state.heat === 0 ? Math.hypot(fv.x, fv.z) : 0;
    // Despawn: far away, or retired/stuck while hidden.
    for (let i = this.list.length - 1; i >= 0; i--) {
      const a = this.list[i]!;
      const v = a.vehicle;
      if (a.persistent) continue;
      const d = Math.hypot(v.pos.x - f.x, v.pos.z - f.z);
      if (d > DESPAWN_R) {
        this.release(a, true);
      } else if (fSpeed > FOCUS_MOVING && d > BEHIND_R && a.mode !== 'wreck' && ((v.pos.x - f.x) * fv.x + (v.pos.z - f.z) * fv.z) / (d * fSpeed) < -0.5) {
        // Well behind a driving player: the budget is better spent on the road ahead.
        if (!this.view.isVisible(v.pos.x, v.pos.z, 5)) this.release(a, true);
      } else if (d > 40 && (a.retire || a.mode === 'wreck' || this.list.length > this.target + MAX_WRECKS)) {
        if (a.mode === 'wreck' && now - a.bornAt < 40) continue;
        if (!this.view.isVisible(v.pos.x, v.pos.z, 5)) this.release(a, true);
      }
    }
    // Cap wrecks.
    let wrecks = 0;
    for (const a of this.list) if (a.mode === 'wreck') wrecks++;
    if (wrecks > MAX_WRECKS) {
      for (const a of this.list) {
        if (a.mode === 'wreck' && !this.view.isVisible(a.vehicle.pos.x, a.vehicle.pos.z, 5)) {
          this.release(a, true);
          break;
        }
      }
    }
    // Trim excess cars (density lowered, quieter streets) once they're out of sight.
    this.ringLanes(false);
    const excess = this.countAmbient() - this.localTarget;
    if (excess > 0) {
      for (const a of this.list) {
        if (a.mode === 'wreck' || a.persistent) continue;
        const v = a.vehicle;
        if (Math.hypot(v.pos.x - f.x, v.pos.z - f.z) > 45 && !this.view.isVisible(v.pos.x, v.pos.z, 5)) {
          this.release(a, true);
          break;
        }
      }
    }
    // Spawn up to one car per tick (more while the streets are thin: fast driving, a long chase).
    const target = this.localTarget;
    let have = this.countAmbient();
    if (have < target) {
      const budget = have < target * 0.6 ? 3 : have < target * 0.85 ? 2 : 1;
      let spawned = 0;
      for (let tries = 0; tries < 8 * budget && spawned < budget && have < target; tries++) {
        if (this.trySpawnRandom(false)) {
          spawned++;
          have++;
        }
      }
    }
  }

  /**
   * The focus jumped (teleport, respawn, cut): every non-persistent car beyond the despawn radius is
   * out of the player's world already, so drop it now instead of on the next manage() tick. Without
   * this the stale cars still count towards the target and `fillInitial()` adds nothing.
   */
  releaseFar(radius = DESPAWN_R): number {
    const f = this.focus();
    let n = 0;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const a = this.list[i]!;
      if (a.persistent) continue;
      if (Math.hypot(a.vehicle.pos.x - f.x, a.vehicle.pos.z - f.z) > radius) {
        this.release(a, true);
        n++;
      }
    }
    return n;
  }

  /** Fill the streets up to the target immediately (game start / density change / focus jump). */
  fillInitial(): void {
    let guard = 0;
    this.ringLanes(true);
    while (this.countAmbient() < this.localTarget && guard++ < 600) this.trySpawnRandom(true);
  }

  // ----------------------------------------------------------------- path geometry

  /** Point `dist` metres ahead along the agent's path (current lane remainder -> connector -> next lane). */
  private sample(a: TrafficAgent, dist: number, out: PathSample): void {
    const lane = a.lane;
    let d = dist;
    if (a.phase === 'lane') {
      const rem = lane.length - a.s;
      if (d <= rem || !a.connector) {
        const s = Math.min(a.s + d, lane.length);
        // The path starts where the car actually is and drifts onto the shifted line over ~12 m;
        // the shift fades out again towards the junction.
        const mix = clamp(dist / (a.mode === 'bypass' ? 5 : 12), 0, 1);
        const sh = (a.lat + (a.shift - a.lat) * mix) * clamp((lane.length - s - 2) / 10, 0, 1);
        out.x = lane.a.x + lane.dir.x * s + lane.left.x * sh;
        out.z = lane.a.z + lane.dir.z * s + lane.left.z * sh;
        out.dx = lane.dir.x;
        out.dz = lane.dir.z;
        return;
      }
      d -= rem;
      this.sampleConnector(a.connector, d, a.next!, out);
      return;
    }
    const c = a.connector!;
    this.sampleConnector(c, a.s + d, a.next!, out);
  }

  private sampleConnector(c: Connector, d: number, next: Lane, out: PathSample): void {
    if (d <= c.length) {
      const pts = c.pts;
      const cum = c.cum;
      let i = 0;
      while (i < pts.length - 2 && cum[i + 1]! < d) i++;
      const seg = cum[i + 1]! - cum[i]!;
      const u = seg > 1e-6 ? clamp((d - cum[i]!) / seg, 0, 1) : 0;
      const p0 = pts[i]!;
      const p1 = pts[i + 1]!;
      out.x = p0.x + (p1.x - p0.x) * u;
      out.z = p0.z + (p1.z - p0.z) * u;
      const l = Math.hypot(p1.x - p0.x, p1.z - p0.z) || 1;
      out.dx = (p1.x - p0.x) / l;
      out.dz = (p1.z - p0.z) / l;
      return;
    }
    const e = Math.min(d - c.length, next.length);
    out.x = next.a.x + next.dir.x * e;
    out.z = next.a.z + next.dir.z * e;
    out.dx = next.dir.x;
    out.dz = next.dir.z;
  }

  /** Update lane/junction phase and progress `s` from the car's actual position. */
  private track(a: TrafficAgent): void {
    const p = a.vehicle.pos;
    if (a.phase === 'lane') {
      const lane = a.lane;
      a.s = (p.x - lane.a.x) * lane.dir.x + (p.z - lane.a.z) * lane.dir.z;
      a.lat = (p.x - lane.a.x) * lane.left.x + (p.z - lane.a.z) * lane.left.z;
      const lateral = Math.abs(a.lat - a.shift);
      if (lateral > 6 || a.s < -8) {
        this.reacquire(a);
        return;
      }
      if (a.s >= lane.length - 0.2 && a.connector) {
        a.phase = 'junction';
        a.connIdx = 0;
        a.s = 0;
        this.advanceConnector(a);
      }
      return;
    }
    this.advanceConnector(a);
    const c = a.connector!;
    if (a.s >= c.length - 0.5) {
      a.lane = a.next!;
      a.phase = 'lane';
      a.s = Math.max(0, (p.x - a.lane.a.x) * a.lane.dir.x + (p.z - a.lane.a.z) * a.lane.dir.z);
      if (a.route) a.routeIdx++;
      a.shift = 0;
      this.chooseNext(a);
    }
  }

  private advanceConnector(a: TrafficAgent): void {
    const c = a.connector!;
    const p = a.vehicle.pos;
    let best = a.connIdx;
    let bestD = Infinity;
    const last = Math.min(a.connIdx + 3, c.pts.length - 2);
    for (let i = Math.max(a.connIdx - 1, 0); i <= last; i++) {
      const p0 = c.pts[i]!;
      const p1 = c.pts[i + 1]!;
      const ex = p1.x - p0.x;
      const ez = p1.z - p0.z;
      const l2 = ex * ex + ez * ez || 1;
      const t = clamp(((p.x - p0.x) * ex + (p.z - p0.z) * ez) / l2, 0, 1);
      const qx = p0.x + ex * t - p.x;
      const qz = p0.z + ez * t - p.z;
      const dd = qx * qx + qz * qz;
      if (dd < bestD) {
        bestD = dd;
        best = i;
      }
    }
    a.connIdx = best;
    const p0 = c.pts[best]!;
    const p1 = c.pts[best + 1]!;
    const ex = p1.x - p0.x;
    const ez = p1.z - p0.z;
    const l2 = ex * ex + ez * ez || 1;
    const t = clamp(((p.x - p0.x) * ex + (p.z - p0.z) * ez) / l2, 0, 1);
    a.s = c.cum[best]! + t * (c.cum[best + 1]! - c.cum[best]!);
  }

  /** The car ended up somewhere unexpected (shunted, spun): re-attach to the nearest matching lane. */
  private reacquire(a: TrafficAgent): void {
    const v = a.vehicle;
    const hit = this.net.nearestLane(v.pos, v.heading);
    if (!hit) return;
    a.lane = hit.lane;
    a.phase = 'lane';
    a.s = hit.s;
    a.shift = 0;
    a.route = null;
    this.chooseNext(a);
  }

  // ----------------------------------------------------------------- the driver

  private think(a: TrafficAgent, dt: number, now: number): void {
    const v = a.vehicle;
    const spec = v.spec;
    const halfLen = spec.length / 2;
    const halfW = spec.width / 2;
    // Plan braking within what this vehicle can actually do (a loaded tipper is not a Minnow).
    const bComfort = Math.min(BRAKE_COMFORT, spec.brake * 0.55);
    const bFollow = Math.min(BRAKE_FIRM * 0.5, spec.brake * 0.42);
    const sinH = Math.sin(v.heading);
    const cosH = Math.cos(v.heading);
    const vF = v.velocity.x * sinH + v.velocity.z * cosH;
    a.vF = vF;

    // ---- destroyed / abandoned
    if (v.destroyed || (v.health < v.spec.maxHealth * 0.25 && v.speed < 1.5)) {
      this.abandon(a, now);
      return;
    }

    this.track(a);

    // ---- modes with their own control
    if (a.mode === 'shaken') {
      if (now < a.shakenUntil) {
        this.output(a, 0, 0, true);
        return;
      }
      a.mode = 'drive';
    }
    if (a.mode === 'recover') {
      if (now < a.recoverUntil) {
        this.output(a, -0.65, a.recoverSteer, false);
        return;
      }
      a.mode = 'drive';
      a.stuckTime = 0;
      this.reacquire(a);
    }

    if (now < a.backoffUntil) {
      this.output(a, -0.5, 0, false);
      return;
    }

    let target = a.lane.speedLimit * a.speedFactor;
    if (a.phase === 'junction') target = Math.min(target, a.connector!.maxSpeed);
    if (a.lane.alley) target = Math.min(target, 5.5);
    let hold: TrafficHold = 'none';
    const panicking = now < a.panicUntil;
    if (panicking) target *= 1.2;

    // ---- threats: dangerous player driving, sirens
    this.reactToThreats(a, now, vF, halfW);
    if (a.mode === 'pullOver') {
      const siren = now - a.sirenSeenAt < 2.5;
      if (!siren) {
        a.mode = 'drive';
        a.shiftTarget = 0;
      } else {
        a.shiftTarget = this.kerbShift(a, halfW);
        const toLine = a.phase === 'lane' ? a.lane.length - a.s : 0;
        // Never park inside a junction; crawl until clear of it.
        if (a.phase === 'lane' && toLine > halfLen + 7) {
          // Slow while angling in to the kerb, stop once tucked in.
          const tucked = Math.abs(a.lat - a.shiftTarget) < 0.5;
          target = tucked ? 0 : Math.min(target, Math.max(3, vF * 0.6));
          hold = 'siren';
        } else {
          target = Math.min(target, 3);
        }
      }
    }
    // Going around something: creep until we have actually moved over.
    if (a.mode === 'bypass' && Math.abs(a.lat - a.shiftTarget) > 1.1) target = Math.min(target, 2.6);
    // Ease the lateral shift.
    a.shift += clamp(a.shiftTarget - a.shift, -2.4 * dt, 2.4 * dt);

    // ---- junction approach: corner speed, signals, right of way
    if (a.phase === 'lane' && a.connector) {
      const dLine = a.lane.length - a.s;
      const stopDist = dLine - halfLen - 0.9;
      const c = a.connector;
      if (c.kind !== 'straight' || a.lane.alley || c.to.alley) {
        const vAllow = Math.sqrt(c.maxSpeed * c.maxSpeed + 2 * bComfort * Math.max(0, dLine - halfLen));
        target = Math.min(target, vAllow);
      }
      const stop = this.mustStop(a, vF, stopDist, now, panicking);
      if (stop !== 'none') {
        const allowed = this.brakeSpeed(stopDist, bComfort);
        if (allowed < target) {
          target = allowed;
          hold = stop;
        }
        if (vF < 0.6 && stopDist < 2.5) a.waitTime += dt;
      } else {
        a.waitTime = 0;
      }
    } else {
      a.waitTime = 0;
    }

    // ---- obstacles on the path ahead
    let look = clamp(9 + Math.abs(vF) * 1.8, 11, 36);
    if (a.phase === 'lane' && a.connector) {
      // Near a junction, look right through it so we never enter a box we can't leave.
      const toLine = a.lane.length - a.s;
      if (toLine < 22) look = Math.max(look, Math.min(toLine + a.connector.length + spec.length + 5, 48));
    }
    const obstacle = this.scanAhead(a, look, halfLen, halfW);
    if (obstacle) {
      const minGap = 1.9 + Math.max(vF, 0) * 0.28;
      const lead = Math.max(obstacle.speed, 0);
      let allowed: number;
      if (obstacle.gap <= minGap * 0.5) allowed = Math.max(0, lead - 1.5);
      else allowed = lead + Math.sqrt(2 * bFollow * Math.max(0, obstacle.gap - minGap));
      if (obstacle.kind === 'ped' && obstacle.gap < minGap + 2) allowed = 0;
      // Don't block a junction: if the queue ahead sits beyond the stop line, hold at the line.
      if (a.phase === 'lane' && a.connector && obstacle.beyondLine && obstacle.speed < 2) {
        const stopDist = a.lane.length - a.s - halfLen - 0.9;
        allowed = Math.min(allowed, this.brakeSpeed(stopDist, bComfort));
        if (hold === 'none') hold = 'junction';
      } else if (allowed < target && hold === 'none') {
        hold = obstacle.kind === 'ped' ? 'ped' : 'lead';
      }
      target = Math.min(target, allowed);
      if (obstacle.kind === 'vehicle' && obstacle.stationary && vF < 0.5 && obstacle.vehicle) {
        a.blockedTime += dt;
        this.blockedBehaviour(a, now, obstacle.vehicle, obstacle.gap);
      } else if (vF >= 0.5) {
        a.blockedTime = 0;
      }
    } else {
      a.blockedTime = 0;
      if (a.mode === 'bypass' && now > a.modeUntil) {
        a.mode = 'drive';
        a.shiftTarget = 0;
      }
    }

    // ---- box blocked by something that won't move (abandoned / wrecked car): give up on that exit
    if (a.phase === 'lane' && a.connector && obstacle?.vehicle && obstacle.stationary && vF < 0.5 && (hold === 'junction' || hold === 'lead') && a.lane.length - a.s < 14) {
      const ba = this.byVehicle.get(obstacle.vehicle.id);
      if (!ba || ba.mode === 'wreck') {
        // Somebody already gave up on this path: don't wait the full time again.
        a.boxWait += a.boxWait >= 0 && this.isBlocked(a.connector) ? BOX_REROUTE_S : dt;
        if (a.boxWait >= BOX_REROUTE_S) {
          // No clear way out: look again in a while (retirement handles it if it never clears).
          if (this.rerouteAroundBlock(a, halfW)) hold = 'none';
          else a.boxWait = -BOX_REROUTE_S;
        }
      }
    } else if (vF > 1) {
      a.boxWait = 0;
    }

    a.hold = hold;

    // ---- gridlock breakers: un-ghost once clear, ghost a stalled box, retire long jams
    if (a.ghost && a.phase === 'lane' && a.s > 9) this.setGhost(a, false);
    if (vF < 0.3 && (hold === 'lead' || hold === 'junction' || hold === 'yield')) {
      a.jamTime += dt;
      if (a.phase === 'junction' && !a.ghost && a.blockedTime > 9 && obstacle?.vehicle && this.byVehicle.has(obstacle.vehicle.id)) this.setGhost(a, true);
      if (a.jamTime > 45) a.retire = true;
    } else if (vF > 1) {
      a.jamTime = 0;
    }

    // ---- stuck detection (only when nothing legitimate is holding us)
    if (hold === 'none' && target > 1.5 && vF < 0.4) {
      a.stuckTime += dt;
      if (a.stuckTime > 2.6) this.startRecovery(a, now);
    } else if (vF > 1) {
      a.stuckTime = 0;
    }
    if (a.waitTime > 11 && hold !== 'signal') {
      // Deadlock breaker at a junction: stop yielding.
      a.waitTime = 0;
      a.stuckTime = 0;
    }

    this.drive(a, target, vF, spec.wheelbase, spec.steerAngle, spec.steerFalloffSpeed, panicking);
  }

  private brakeSpeed(dist: number, b: number): number {
    return dist <= 0.15 ? 0 : Math.sqrt(2 * b * dist);
  }

  private output(a: TrafficAgent, throttle: number, steer: number, handbrake: boolean): void {
    const c = a.vehicle.controls;
    // The vehicle system skips sleeping bodies; a car that waited at a light must be woken to move.
    if (throttle !== 0 && a.vehicle.body.sleeping) a.vehicle.body.wake();
    c.throttle = throttle;
    c.steer = steer;
    c.handbrake = handbrake;
  }

  private drive(a: TrafficAgent, target: number, vF: number, wheelbase: number, steerAngle: number, falloff: number, eager: boolean): void {
    const v = a.vehicle;
    // Steering: pure pursuit towards a point ahead on the path.
    const look = clamp(3.2 + Math.abs(vF) * 0.5, 3.6, 11);
    const t = this.tmp;
    this.sample(a, look, t);
    const dx = t.x - v.pos.x;
    const dz = t.z - v.pos.z;
    const sinH = Math.sin(v.heading);
    const cosH = Math.cos(v.heading);
    const lz = dx * sinH + dz * cosH;
    const lx = -dx * cosH + dz * sinH; // + = target on the right
    const alpha = Math.atan2(lx, Math.max(lz, 0.05));
    const ld = Math.max(Math.hypot(lx, lz), 1);
    const delta = Math.atan((2 * wheelbase * Math.sin(alpha)) / ld);
    const speed = Math.abs(vF);
    const authority = steerAngle / (1 + (speed / falloff) ** 2);
    const steer = clamp(delta / authority, -1, 1);
    a.lastSteer = steer;

    // Speed: gentle P-controller.
    let throttle = 0;
    let handbrake = false;
    if (target < 0.25 && vF < 0.7) {
      handbrake = true;
    } else {
      const err = target - vF;
      if (err > 0) throttle = clamp(err * 0.7, 0, eager ? 0.95 : 0.62);
      else throttle = vF < 0.5 ? 0 : clamp(err * 0.9, -1, 0);
    }
    this.output(a, throttle, steer, handbrake);
  }

  // ----------------------------------------------------------------- decisions

  /** Should this car stop at the line ahead? Returns the reason or 'none'. */
  private mustStop(a: TrafficAgent, vF: number, stopDist: number, now: number, panicking: boolean): TrafficHold {
    const lane = a.lane;
    const ixId = lane.endIntersection;
    if (!a.next || !a.connector) return 'junction'; // dead end: wait for a way out
    if (ixId === null) return 'none';
    const committed = stopDist < -1.2; // nose clearly over the line (stopDist is measured to 0.9 m before it)
    if (committed) return 'none';
    const ix = this.layout.intersections[ixId]!;
    let colour = ix.signal ? this.signals.colourFor(ixId, lane.edgeId, now) : 'green';
    if (panicking && colour === 'amber') colour = 'green';
    if (colour === 'red') return 'signal';
    if (colour === 'amber') {
      const brakeDist = (vF * vF) / (2 * Math.min(BRAKE_COMFORT, a.vehicle.spec.brake * 0.55));
      if (brakeDist <= Math.max(0, stopDist) + 0.5) return 'signal';
    }
    a.yieldTo = 0;
    if (a.waitTime > 9) a.forceGoUntil = now + 6;
    if (now < a.forceGoUntil) return 'none';
    // Right of way: wait for conflicting cars already in the box, or higher-priority arrivals.
    const conn = a.connector;
    const n = lane.toNode;
    const occ = this.occupants[n]!;
    for (let i = 0; i < occ.length; i++) {
      const o = occ[i]!;
      if (o === a || !o.connector) continue;
      if (this.net.conflicts(conn, o.connector)) {
        a.yieldTo = o.id;
        return 'yield';
      }
    }
    const apps = this.approachers[n]!;
    const signalled = ix.signal != null;
    const myPrio = this.priority(a, signalled);
    // Time I need to be fully through the box from where I stand.
    const myClear = (Math.max(lane.length - a.s, 0) + conn.length + a.vehicle.spec.length) / Math.max(Math.min(vF + 2, conn.maxSpeed), 2.5);
    for (let i = 0; i < apps.length; i++) {
      const o = apps[i]!;
      if (o === a || o.lane === a.lane || !o.connector) continue;
      if (o.hold === 'signal') continue;
      if (ix.signal && this.signals.colourFor(ixId, o.lane.edgeId, now) === 'red') continue;
      if (!this.net.conflicts(conn, o.connector)) continue;
      const oPrio = this.priority(o, signalled);
      const oEta = Math.max(o.lane.length - o.s, 0) / Math.max(o.vF, 1.5);
      if (oPrio > myPrio && oEta < myClear + 1) {
        a.yieldTo = -o.id;
        return 'yield';
      }
    }
    return 'none';
  }

  /**
   * Right-of-way rank. Unsignalled: the bigger road first, then straight > left > right. Signalled:
   * approaches that share a green are equals, so only the movement counts (a right-turner gives way to
   * oncoming straight-on and left-turning traffic, whatever the road widths).
   */
  private priority(a: TrafficAgent, signalled = false): number {
    const edge = this.layout.roads.edges[a.lane.edgeId]!;
    const c = a.connector!;
    const turnRank = c.kind === 'straight' ? 3 : c.kind === 'left' ? 2 : 1;
    // Lower agent id wins ties.
    return (signalled ? 0 : edge.width * 1000) + turnRank * 100 - a.id * 0.001;
  }

  /** Walk probes along the path and return the nearest obstacle (vehicle, player on foot or ped). */
  private scanAhead(a: TrafficAgent, look: number, halfLen: number, halfW: number): Obstacle | null {
    const step = look / PROBES;
    const probe = this.probe;
    this.sample(a, 0, probe[0]!);
    for (let k = 1; k <= PROBES; k++) this.sample(a, k * step, probe[k]!);

    const me = a.vehicle;
    const cand = this.cand;
    cand.length = 0;
    this.scanMe = me;
    this.scanGhost = a.ghost;
    this.grid.forEachNear(me.pos.x, me.pos.z, look + 3.5, this.scanVisit);
    this.scanMe = null;

    let bestK = -1;
    let bestSpeed = 0;
    let bestKind: 'vehicle' | 'ped' = 'vehicle';
    let bestVehicle: Vehicle | null = null;
    const reach = halfW * 0.9 + 0.1;
    for (let c = 0; c < cand.length; c++) {
      const o = cand[c]!;
      const ob = o.body;
      const sinA = Math.sin(ob.angle);
      const cosA = Math.cos(ob.angle);
      const hx = o.spec.width / 2 + reach;
      const hz = o.spec.length / 2 + 0.6;
      const last = bestK < 0 ? PROBES : bestK - 1;
      for (let k = 1; k <= last; k++) {
        const pr = probe[k]!;
        const ddx = pr.x - ob.pos.x;
        const ddz = pr.z - ob.pos.z;
        const lx = ddx * cosA - ddz * sinA;
        const lz = ddx * sinA + ddz * cosA;
        if (Math.abs(lx) < hx && Math.abs(lz) < hz) {
          bestK = k;
          bestKind = 'vehicle';
          bestVehicle = o;
          bestSpeed = o.velocity.x * pr.dx + o.velocity.z * pr.dz;
          break;
        }
      }
    }

    // Probes are up to ~4 m apart: bisect between the last clear probe and the hit for a true gap.
    let vehicleDist = bestK < 0 ? Infinity : bestK * step;
    if (bestVehicle) {
      const ob = bestVehicle.body;
      const sinA = Math.sin(ob.angle);
      const cosA = Math.cos(ob.angle);
      const hx = bestVehicle.spec.width / 2 + reach;
      const hz = bestVehicle.spec.length / 2 + 0.6;
      let lo = (bestK - 1) * step;
      let hi = bestK * step;
      const t = this.tmp;
      for (let it = 0; it < 6; it++) {
        const mid = (lo + hi) / 2;
        this.sample(a, mid, t);
        const ddx = t.x - ob.pos.x;
        const ddz = t.z - ob.pos.z;
        const lx = ddx * cosA - ddz * sinA;
        const lz = ddx * sinA + ddz * cosA;
        if (Math.abs(lx) < hx && Math.abs(lz) < hz) hi = mid;
        else lo = mid;
      }
      // Probe entered the box `0.6 m` before the body: hand that back.
      vehicleDist = hi + 0.6;
    }

    // Pedestrians (and the player on foot) only matter close up.
    const peds = this.ctx.services.tryGet('peds') as PedService | undefined;
    const pedReach = halfW + 0.55;
    const pedLimit = Math.min(bestK < 0 ? PROBES : bestK - 1, Math.ceil(16 / step) + 1);
    if (peds && peds.active > 0) {
      for (let k = 1; k <= pedLimit; k++) {
        const pr = probe[k]!;
        this.pedProbe = pr;
        this.pedReach2 = pedReach * pedReach;
        this.pedHit = false;
        peds.forEachNear(pr.x, pr.z, pedReach, this.pedVisit);
        if (this.pedHit) {
          bestK = k;
          bestKind = 'ped';
          bestVehicle = null;
          bestSpeed = 0;
          break;
        }
      }
    }
    const player = this.ctx.services.tryGet('player') as PlayerService | undefined;
    if (player && player.mode === 'foot') {
      const pp = player.pos;
      const last = bestK < 0 ? PROBES : bestK - 1;
      for (let k = 1; k <= last; k++) {
        const pr = probe[k]!;
        if ((pp.x - pr.x) ** 2 + (pp.z - pr.z) ** 2 < pedReach * pedReach) {
          bestK = k;
          bestKind = 'ped';
          bestVehicle = null;
          bestSpeed = 0;
          break;
        }
      }
    }
    // Static geometry / props on the lane section ahead (never looks past the stop line).
    const dLine = a.phase === 'lane' ? a.lane.length - a.s : -1;
    if (a.phase === 'lane') {
      if ((this.step + a.id) % 3 === 0) {
        const reachLimit = Math.min(look, dLine - halfLen - 0.5);
        a.staticGap = reachLimit > 1.5 ? this.castStatic(me, halfLen, halfW, reachLimit) : Infinity;
      }
      const sg = a.staticGap;
      const vehicleGap = bestK < 0 ? Infinity : bestK * step - halfLen;
      if (sg < vehicleGap) return this.obstacle(sg, 0, 'static', true, false, null);
    } else {
      a.staticGap = Infinity;
    }
    if (bestK < 0) return null;
    const dist = bestKind === 'vehicle' ? vehicleDist : bestK * step;
    return this.obstacle(dist - halfLen, bestSpeed, bestKind, bestVehicle !== null && bestVehicle.speed < 0.6, a.phase === 'lane' && dist > dLine + 0.5, bestVehicle);
  }

  /** scanAhead's answer, one reused record: it is read by the caller before the next scan (no per-agent garbage). */
  private obstacle(gap: number, speed: number, kind: Obstacle['kind'], stationary: boolean, beyondLine: boolean, vehicle: Vehicle | null): Obstacle {
    const o = this.scanResult;
    o.gap = gap;
    o.speed = speed;
    o.kind = kind;
    o.stationary = stationary;
    o.beyondLine = beyondLine;
    o.vehicle = vehicle;
    return o;
  }

  private castStatic(me: Vehicle, halfLen: number, halfW: number, maxDist: number): number {
    const fx = Math.sin(me.heading);
    const fz = Math.cos(me.heading);
    const rx = -fz;
    const rz = fx;
    let best = Infinity;
    for (const side of [-0.6, 0.6]) {
      const off = side * halfW;
      const origin = { x: me.pos.x + fx * halfLen + rx * off, z: me.pos.z + fz * halfLen + rz * off };
      const hit = this.ctx.physics.raycast(origin, { x: fx, z: fz }, maxDist, { mask: Layer.PROP });
      if (hit && hit.distance < best) best = hit.distance;
    }
    return best;
  }

  private kerbShift(a: TrafficAgent, halfW: number): number {
    const edge = this.layout.roads.edges[a.lane.edgeId]!;
    if (edge.width < 6.5) return 0;
    return clamp(a.lane.kerbOffset - halfW - 0.45, 0, 2.4);
  }

  private reactToThreats(a: TrafficAgent, now: number, vF: number, halfW: number): void {
    const v = a.vehicle;
    // Sirens.
    const sirens = this.grid.sirens;
    for (let i = 0; i < sirens.length; i++) {
      const s = sirens[i]!;
      if (s === v) continue;
      const dx = s.pos.x - v.pos.x;
      const dz = s.pos.z - v.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < SIREN_REACT_R * SIREN_REACT_R) {
        // Only make way for an emergency vehicle that is actually coming: moving, heading our way
        // and in sight. A unit parked at an incident, or one on the street behind the terrace, is not.
        if (s.speed < SIREN_MIN_SPEED) continue;
        const d = Math.sqrt(d2) || 1;
        const closing = -(s.velocity.x * dx + s.velocity.z * dz) / d;
        if (closing < SIREN_MIN_CLOSING) continue;
        if (!this.ctx.physics.lineOfSight(s.pos, v.pos)) continue;
        a.sirenSeenAt = now;
        if (a.mode === 'drive' || a.mode === 'bypass') {
          a.mode = 'pullOver';
          if (now >= a.reactReadyAt) {
            a.reactReadyAt = now + 4;
            this.ctx.events.emit('trafficReaction', { vehicleId: v.id, kind: 'pullOver', pos: { x: v.pos.x, z: v.pos.z } });
            this.ctx.events.emit('pedPanic', { pos: { x: v.pos.x, z: v.pos.z }, radius: 14, reason: 'siren' });
          }
        }
        break;
      }
    }
    // A fast-approaching player: get out of the way, honk if it was close.
    const pl = this.playerVehicle;
    if (pl && pl !== v && a.mode === 'drive') {
      const rx = v.pos.x - pl.pos.x;
      const rz = v.pos.z - pl.pos.z;
      const d2 = rx * rx + rz * rz;
      if (d2 < 30 * 30 && pl.speed > 12) {
        const d = Math.sqrt(d2) || 1;
        const closing = (pl.velocity.x * rx + pl.velocity.z * rz) / d;
        if (closing > 9 && closing > pl.speed * 0.75) {
          a.shiftTarget = this.kerbShift(a, halfW);
          if (now >= a.reactReadyAt) {
            a.reactReadyAt = now + 3;
            this.ctx.events.emit('trafficReaction', { vehicleId: v.id, kind: 'swerve', pos: { x: v.pos.x, z: v.pos.z } });
          }
          if (d < 10 && now >= a.honkReadyAt) this.honk(a, now, 'nearMiss');
          return;
        }
      }
    }
    if (a.mode === 'drive' && a.shiftTarget !== 0 && vF >= 0) a.shiftTarget = 0;
  }

  private blockedBehaviour(a: TrafficAgent, now: number, blocker: Vehicle, gap: number): void {
    const blockerAgent = this.byVehicle.get(blocker.id);
    const isTraffic = blockerAgent !== undefined && blockerAgent.mode !== 'wreck';
    // Queues at a red light are normal; only honk at things that are really in the way.
    if (a.blockedTime > 2.2 && now >= a.honkReadyAt && (!isTraffic || blockerAgent.stuckTime > 2)) this.honk(a, now, 'blocked');
    // A stationary obstacle that isn't a traffic car or a signal queue: go around it.
    const edge = this.layout.roads.edges[a.lane.edgeId]!;
    if (a.blockedTime > 7 && !isTraffic && a.phase === 'lane' && edge.width >= 8 && a.mode === 'drive') {
      a.mode = 'bypass';
      a.modeUntil = now + 6;
      // Too close to swing out: back up a little first (if nobody is right behind us).
      if (gap < 4 && !this.somethingBehind(a)) a.backoffUntil = now + 1.9;
      a.shiftTarget = -(a.lane.laneWidth * 0.8 + a.vehicle.spec.width * 0.5);
      this.ctx.events.emit('trafficReaction', { vehicleId: a.vehicle.id, kind: 'bypass', pos: { x: a.vehicle.pos.x, z: a.vehicle.pos.z } });
    }
  }

  private somethingBehind(a: TrafficAgent): boolean {
    const v = a.vehicle;
    const bx = -Math.sin(v.heading);
    const bz = -Math.cos(v.heading);
    let found = false;
    this.grid.forEachNear(v.pos.x + bx * 5, v.pos.z + bz * 5, 4, (o) => {
      if (found || o === v) return;
      const dx = o.pos.x - v.pos.x;
      const dz = o.pos.z - v.pos.z;
      const along = dx * bx + dz * bz;
      if (along > 0 && along < v.spec.length / 2 + o.spec.length / 2 + 3 && Math.abs(dx * bz - dz * bx) < 2.2) found = true;
    });
    return found;
  }

  private honk(a: TrafficAgent, now: number, reason: 'blocked' | 'nearMiss' | 'crash' | 'siren'): void {
    a.honkReadyAt = now + 3.5 + this.rng.range(0, 2);
    this.stats.honks++;
    this.ctx.events.emit('horn', { vehicleId: a.vehicle.id, pos: { x: a.vehicle.pos.x, z: a.vehicle.pos.z }, reason });
  }

  private startRecovery(a: TrafficAgent, now: number): void {
    a.recoveries++;
    this.stats.stuckRecoveries++;
    a.mode = 'recover';
    a.recoverUntil = now + 1.3;
    a.recoverSteer = -Math.sign(a.lastSteer || 1);
    this.ctx.events.emit('trafficReaction', { vehicleId: a.vehicle.id, kind: 'stuck', pos: { x: a.vehicle.pos.x, z: a.vehicle.pos.z } });
    if (a.recoveries >= 3) a.retire = true;
  }

  private abandon(a: TrafficAgent, now: number): void {
    const v = a.vehicle;
    if (a.mode === 'wreck') return;
    a.mode = 'wreck';
    a.bornAt = now;
    v.controller = 'none';
    v.occupant = null;
    v.locked = false;
    this.output(a, 0, 0, true);
    this.ctx.events.emit('trafficReaction', { vehicleId: v.id, kind: 'abandon', pos: { x: v.pos.x, z: v.pos.z } });
    this.ctx.events.emit('pedPanic', { pos: { x: v.pos.x, z: v.pos.z }, radius: 22, reason: 'crash' });
    // The driver climbs out and runs.
    const peds = this.ctx.services.tryGet('peds') as PedService | undefined;
    if (peds) {
      const r = v.body.right();
      peds.spawnFleeing({ x: v.pos.x + r.x * (v.spec.width / 2 + 0.7), z: v.pos.z + r.z * (v.spec.width / 2 + 0.7) }, v.pos);
    }
  }

  // ----------------------------------------------------------------- collision reactions

  onCollision(vehicleId: number, otherVehicleId: number | null, impulse: number, pos: V2): void {
    const a = this.byVehicle.get(vehicleId);
    if (!a || a.mode === 'wreck') return;
    const now = this.ctx.state.time;
    const dv = impulse / a.vehicle.spec.mass;
    if (dv >= 0.8) {
      // Repeatedly running into the same thing (a wall it can't see): back off and re-route.
      a.bumps = now - a.lastBumpAt < 8 ? a.bumps + 1 : 1;
      a.lastBumpAt = now;
      if (a.bumps >= 3 && a.mode !== 'recover') {
        a.bumps = 0;
        this.startRecovery(a, now);
        return;
      }
    }
    if (dv < 1.2) return;
    a.shakenUntil = now + 0.9 + Math.min(dv * 0.3, 2.6);
    if (a.mode !== 'shaken') a.mode = 'shaken';
    if (otherVehicleId !== null && dv > 2 && now >= a.honkReadyAt) this.honk(a, now, 'crash');
    if (dv > 3) this.ctx.events.emit('pedPanic', { pos, radius: 20, reason: 'crash' });
    const other = otherVehicleId === null ? undefined : this.vehicles.get(otherVehicleId);
    // A nudge (under CAR_BUMP_MIN_DV of shove) is an accident: a honk, no crime (docs/POLICE.md §1).
    if (other && other.controller === 'player' && dv >= CAR_BUMP_MIN_DV && now >= a.crimeReadyAt) {
      a.crimeReadyAt = now + 2.5;
      this.ctx.events.emit('crime', {
        type: 'vehicleDamage',
        pos: { x: pos.x, z: pos.z },
        severity: dv > 6 ? 2 : 1,
        byPlayer: true,
        vehicleId: other.id,
      });
      if (dv > 5) a.panicUntil = now + 5;
    } else if (other && other.controller !== 'player' && dv >= 3 && now >= a.crimeReadyAt) {
      // NPC-on-NPC smash: nobody's wanted for it, but a patrol may come and have a look.
      a.crimeReadyAt = now + 5;
      this.ctx.events.emit('crime', {
        type: 'vehicleDamage',
        pos: { x: pos.x, z: pos.z },
        severity: dv >= 5 ? 2 : 1,
        byPlayer: false,
        vehicleId: a.vehicle.id,
      });
    }
  }

  /** Panic nearby drivers (wreck, hit pedestrian, etc.). */
  panicNear(pos: V2, radius: number): void {
    const now = this.ctx.state.time;
    for (const a of this.list) {
      if (a.mode === 'wreck') continue;
      const dx = a.vehicle.pos.x - pos.x;
      const dz = a.vehicle.pos.z - pos.z;
      if (dx * dx + dz * dz < radius * radius) {
        a.panicUntil = now + 4;
        if (now >= a.reactReadyAt) {
          a.reactReadyAt = now + 3;
          this.ctx.events.emit('trafficReaction', { vehicleId: a.vehicle.id, kind: 'panic', pos: { x: a.vehicle.pos.x, z: a.vehicle.pos.z } });
        }
      }
    }
  }

  /**
   * A vehicle blew up: everyone in earshot panics; drivers right next to it with the fireball in front
   * of them back away first (if nothing is behind them).
   */
  explosionNear(pos: V2, radius: number, backOffRadius = 15): void {
    this.panicNear(pos, radius);
    const now = this.ctx.state.time;
    for (const a of this.list) {
      if (a.mode === 'wreck') continue;
      const v = a.vehicle;
      const dx = pos.x - v.pos.x;
      const dz = pos.z - v.pos.z;
      if (dx * dx + dz * dz > backOffRadius * backOffRadius) continue;
      if (dx * Math.sin(v.heading) + dz * Math.cos(v.heading) <= 0) continue;
      if (!this.somethingBehind(a)) a.backoffUntil = now + 1.6;
    }
  }

  dispose(): void {
    this.clear();
  }
}
